<script lang="ts">
import { defineComponent, PropType, ref, computed, nextTick } from 'vue'
import ValidationObserver from '@/components/validation/ValidationObserver.vue'
import validationOperator from '@/models/validationOperator'
import { useClientStore } from '@/stores/client'
import { useErrorStore } from '@/stores/error'
import { useScreenLockStore } from '@/stores/screenLock'
import { datadogService } from '@/services/datadog'
import type { Ref } from 'vue'

export default defineComponent({
  name: 'FormDefault',

  components: {
    ValidationObserver,
  },

  props: {
    /**
     * form 要素の name 属性値
     * @description 配下の ValidationProvider の namespace と一致させる
     */
    name: {
      type: String,
      default: '',
    },

    /**
     * form 要素の action 属性値
     */
    action: {
      type: String,
      default: '',
    },

    /**
     * form 要素の method 属性値
     */
    method: {
      type: String as PropType<FormMethod>,
      required: true,
      validator(value: string) {
        return ['post', 'get', 'put', 'delete', 'POST', 'GET', 'PUT', 'DELETE'].includes(value)
      },
    },

    /**
     * form 要素の enctype 属性値
     * @description props としての初期値は undefined ですが、
     *              HTML の form 要素の初期値は 'application/x-www-form-urlencoded' ですので、
     *              暗黙的にこれで送られます。
     */
    enctype: {
      type: String as PropType<'application/x-www-form-urlencoded' | 'multipart/form-data' | 'text/plain'>,
      default: undefined,
    },

    /*
     * 画面レンダリング時の初回バリデーションをするかどうか
     * @description 初回バリデーションは結果の表示だけを行い、通過しても送信はしない
     */
    initValidation: {
      type: Boolean,
      default: false,
    },

    /**
     * submit が実際に走る前に発火する任意の関数
     * @description submit 前に何か処理を行いたい場合に使用
     */
    beforeAction: {
      type: Function,
      default() {
        return
      }
    }
  },

  emits: ['error', 'validated'],

  setup(props) {
    // store
    const clientStore = useClientStore()
    const errorStore = useErrorStore()
    const screenLock = useScreenLockStore()

    const formEl: Ref<HTMLFormElement | undefined> = ref()
    const isSubmitted = ref(false)
    const { validationObserver, validate } = validationOperator()

    /**
     * HTML がサポートしている form メソッドかどうか
     *
     * @description HTML の仕様上、POST と GET 以外の方法（PUT, DELETE）で送ることができないため、
     *              PUT、または DELETE で送りたい場合は、form 要素の method 属性値は 'post' で送り、
     *              form 要素内に hidden 要素で「name="_method"」「value="put（もしくは delete）"」を送ることで、任意の method で送ることができる。
     * @see https://portaltan.hatenablog.com/entry/2015/07/22/122031
     */
    const isSupportedMethod = computed<boolean>(() => {
      const method = props.method.toLowerCase()
      return !!(method === 'post' || method === 'get')
    })

    /**
     * フォームの method 属性値
     */
    const formMethod = computed(() => {
      return isSupportedMethod.value
        ? props.method
        : 'post'
    })

    nextTick(() => {
      // 初回バリデーションが有効であれば、画面レンダリング時にバリデーションを発火させる
      if (props.initValidation) {
        validate()
      }
    })

    /**
     * submit 操作を横取りし、バリデーションを通過した場合のみ送信する
     */
    const validateForm = async (e: Event): Promise<void> => {
      e.preventDefault()

      if (await validate()) submit()
    }

    /**
     * form を送信する
     * @description 検査待ちの間に submit 操作が重なっても送信は 1 回だけにする（送信後は画面遷移するため解除しない）
     */
    const submit = (): void => {
      if (isSubmitted.value) return

      // 同一画面の他フォームに残っているエラーでは止めない（自区画とサーバ由来のエラーだけを見る）
      if (errorStore.hasFieldErrorsIn(props.name)) return

      // 常に submit を横取りしているため、action が未解決だと「押しても何も起きないボタン」になる。無言にせず痕跡を残す
      if (!props.action) {
        const message = `[FormDefault] form "${props.name}" の action が空のため送信しませんでした`
        console.error(message)
        datadogService.trackError(new Error(message), { error_type: 'form_default_missing_action', form: props.name })
        return
      }

      if (formEl.value && typeof formEl.value !== 'undefined') {
        isSubmitted.value = true

        screenLock.lockScreen({
          hasBlackOut: false,
          hasBlur: true,
          hasWaitAnimation: true,
          hasScrollLock: false,
        })

        props.beforeAction()

        formEl.value.submit()
      }
    }

    return {
      // store
      clientStore,

      // data
      formEl,
      validationObserver,

      //computed
      isSupportedMethod,
      formMethod,

      // methods
      validateForm,

      // exposed method
      submit,
    }
  }
})
</script>

<template>
  <form
    ref="formEl"
    class="form"
    :name="name || undefined"
    :action="action"
    :method="formMethod"
    :enctype="enctype"
    @submit="validateForm">
    <ValidationObserver
      ref="validationObserver"
      :namespace="name"
      @error="$emit('error', $event)"
      @completed="$emit('validated')">
      <slot />
      <input
        class="input"
        type="hidden"
        :name="clientStore.context.csrfParam"
        :value="clientStore.context.csrfToken">
    </ValidationObserver>

    <!--
      put、もしくは delete メソッドで Form submit する
      @see https://portaltan.hatenablog.com/entry/2015/07/22/122031
    -->
    <input
      v-if="!isSupportedMethod"
      class="input"
      type="hidden"
      name="_method"
      :value="method">
  </form>
</template>
